"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import EpisodeCard from "./episode-card";
import {
  EpisodeWithUser,
  retrieveEpisodes,
} from "@/app/actions/episode/retrieve";
import { Clock5, Eye, Heart, TrendingUp, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Session } from "@/lib/user";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { notoSerif } from "@/lib/fonts";

const PAGE_SIZE = 8;
const INITIAL_SKELETON_COUNT = 8;

type LoadPhase = "initial" | "loadingMore" | "idle" | "error";

interface EpisodeCardsProps {
  category?: string;
  session?: Session | null;
  onFirstEpisodeCoverChange?: (cover: string | null) => void;
}

const EpisodeCards = ({
  category,
  session,
  onFirstEpisodeCoverChange,
}: EpisodeCardsProps) => {
  const [episodesList, setEpisodesList] = useState<EpisodeWithUser[]>([]);
  const [sortBy, setSortBy] = useState<
    "views" | "date" | "trending" | "for_you" | ""
  >("");
  const [loadPhase, setLoadPhase] = useState<LoadPhase>("initial");
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [appendStartIndex, setAppendStartIndex] = useState<number | null>(null);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  const isFetchInFlightRef = useRef(false);
  const lastRequestedPageRef = useRef<number | null>(null);
  const requestTokenRef = useRef(0);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadPhaseRef = useRef<LoadPhase>("initial");
  const episodesListRef = useRef<EpisodeWithUser[]>([]);
  const router = useRouter();

  const seenEpisodeIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (sortBy === "") {
      const initialSort = session?.user && !category ? "for_you" : "trending";
      setSortBy(initialSort);
    }
  }, [session, sortBy, category]);

  const resetPaginationState = useCallback(() => {
    requestTokenRef.current += 1;
    isFetchInFlightRef.current = false;
    lastRequestedPageRef.current = null;
    pageRef.current = 0;
    hasMoreRef.current = true;
    loadPhaseRef.current = "initial";
    episodesListRef.current = [];
    seenEpisodeIds.current.clear();

    setPage(0);
    setHasMore(true);
    setLoadPhase("initial");
    setEpisodesList([]);
    setAppendStartIndex(null);
  }, []);

  const loadPage = useCallback(
    async (targetPage: number, mode: "initial" | "pagination") => {
      if (sortBy === "") return;
      if (isFetchInFlightRef.current) return;
      if (mode !== "initial" && !hasMoreRef.current) return;
      if (lastRequestedPageRef.current === targetPage) return;

      isFetchInFlightRef.current = true;
      lastRequestedPageRef.current = targetPage;

      const requestToken = ++requestTokenRef.current;
      const isInitialLoad = mode === "initial";
      setLoadPhase(isInitialLoad ? "initial" : "loadingMore");
      loadPhaseRef.current = isInitialLoad ? "initial" : "loadingMore";
      if (isInitialLoad) {
        setAppendStartIndex(null);
      }

      const currentCount = isInitialLoad ? 0 : episodesListRef.current.length;

      try {
        const newEpisodes = await retrieveEpisodes(
          sortBy,
          PAGE_SIZE,
          targetPage * PAGE_SIZE,
          category,
          session?.user?.id
        );

        if (requestToken !== requestTokenRef.current) return;

        // Handle empty For You results by switching to trending.
        if (sortBy === "for_you" && targetPage === 0 && newEpisodes.length === 0) {
          toast.info("Follow topics to build your For You feed.", {
            action: {
              label: "Browse",
              onClick: () => router.push("/browse"),
            },
          });
          resetPaginationState();
          setSortBy("trending");
          return;
        }

        const uniqueEpisodes = (newEpisodes as EpisodeWithUser[]).filter(
          (episode) => {
            if (seenEpisodeIds.current.has(episode.id)) {
              return false;
            }
            seenEpisodeIds.current.add(episode.id);
            return true;
          }
        );

        if (!isInitialLoad && uniqueEpisodes.length > 0) {
          setAppendStartIndex(currentCount);
        } else {
          setAppendStartIndex(null);
        }

        const exhaustedSource =
          newEpisodes.length < PAGE_SIZE || uniqueEpisodes.length === 0;
        const nextHasMore = !exhaustedSource;

        setEpisodesList((prev) => {
          const merged = isInitialLoad ? uniqueEpisodes : [...prev, ...uniqueEpisodes];
          episodesListRef.current = merged;
          return merged;
        });

        setHasMore(nextHasMore);
        hasMoreRef.current = nextHasMore;
        setPage(targetPage);
        pageRef.current = targetPage;
        setLoadPhase("idle");
        loadPhaseRef.current = "idle";
      } catch (error) {
        if (requestToken !== requestTokenRef.current) return;
        lastRequestedPageRef.current = null;
        setLoadPhase("error");
        loadPhaseRef.current = "error";
        console.error("Error fetching episodes:", error);
        toast.error("Failed to load episodes");
      } finally {
        if (requestToken === requestTokenRef.current) {
          isFetchInFlightRef.current = false;
        }
      }
    },
    [sortBy, category, session?.user?.id, router, resetPaginationState]
  );

  useEffect(() => {
    if (sortBy === "") return;
    resetPaginationState();
    void loadPage(0, "initial");
  }, [sortBy, category, session?.user?.id, resetPaginationState, loadPage]);

  // Handler for sort changes
  const handleSortChange = (
    newSortBy: "views" | "date" | "trending" | "for_you"
  ) => {
    if (newSortBy === "for_you" && !session?.user) {
      toast.error("Please sign in to see personalized recommendations", {
        action: {
          label: "Sign In",
          onClick: () => router.push("/login"),
        },
      });
      return;
    }

    // Only reset if changing sort method
    if (newSortBy !== sortBy) {
      setSortBy(newSortBy);
    }
  };

  // Intersection Observer for infinite scrolling
  useEffect(() => {
    const currentTarget = observerTarget.current;
    if (!currentTarget || !hasMore) return;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (isFetchInFlightRef.current || !hasMoreRef.current) return;
        if (loadPhaseRef.current === "initial") return;

        observerRef.current?.unobserve(currentTarget);
        void loadPage(pageRef.current + 1, "pagination");
      },
      {
        threshold: 0,
        rootMargin: "0px 0px 320px 0px",
      }
    );

    observerRef.current.observe(currentTarget);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasMore, loadPage, loadPhase, page]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadPhaseRef.current = loadPhase;
  }, [loadPhase]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    episodesListRef.current = episodesList;
  }, [episodesList]);

  useEffect(() => {
    onFirstEpisodeCoverChange?.(episodesList[0]?.cover || null);
  }, [episodesList, onFirstEpisodeCoverChange]);

  return (
    <div className="mt-8">
      <div className="flex flex-row justify-between items-center">
        <h3
          className={`text-lg md:text-xl font-semibold ${notoSerif.className} inline-flex items-center gap-2`}
        >
          {sortBy === "views" ? (
            <Eye />
          ) : sortBy === "date" ? (
            <Clock5 />
          ) : sortBy === "for_you" ? (
            <Heart />
          ) : (
            <TrendingUp />
          )}
          {sortBy === "views"
            ? "Most Viewed"
            : sortBy === "date"
              ? "Latest Bites"
              : sortBy === "for_you"
                ? "For You"
                : "Trending"}
        </h3>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              size="sm"
            >
              {sortBy === "views" ? (
                <Eye />
              ) : sortBy === "date" ? (
                <Clock5 />
              ) : sortBy === "for_you" ? (
                <Heart />
              ) : (
                <TrendingUp />
              )}
              <span className="text-xs md:text-sm font-medium capitalize">
                Sort By:{" "}
                {sortBy === "views"
                  ? "Views"
                  : sortBy === "date"
                    ? "Date"
                    : sortBy === "for_you"
                      ? "For You"
                      : "Trending"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {/* Only show "For You" option when no category is provided */}
            {!category && (
              <DropdownMenuItem onClick={() => handleSortChange("for_you")}>
                <Heart className="h-4 w-4" />
                For You
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => handleSortChange("trending")}>
              <TrendingUp className="h-4 w-4" />
              Trending
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSortChange("views")}>
              <Eye className="h-4 w-4" />
              Views
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSortChange("date")}>
              <Clock5 className="h-4 w-4" />
              Date
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex flex-col xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 md:grid gap-4 mt-4">
        {episodesList.map((episode, index) => (
          <EpisodeCard
            key={episode.id}
            className={
              appendStartIndex !== null && index >= appendStartIndex
                ? "animate-in fade-in slide-in-from-bottom-2 duration-300 motion-reduce:animate-none"
                : undefined
            }
            episode={{
              id: episode.id,
              title: episode.title,
              description: episode.description,
              cover: episode.cover!,
              user: {
                name: episode.user?.name,
                image: episode.user?.image,
                id: episode.user?.id,
                subscriptionStatus: episode.user?.subscriptionStatus, // Pass subscriptionStatus here
              },
              views: episode.views,
              date: episode.date,
            }}
          />
        ))}
        {loadPhase === "initial" &&
          episodesList.length === 0 &&
          Array.from({ length: INITIAL_SKELETON_COUNT }).map((_, index) => (
            <Skeleton
              key={index}
              className="h-[24.5rem] w-full"
            />
          ))}
        <div className="w-full h-14 flex items-center justify-center md:col-span-2 lg:col-span-3 xl:col-span-4">
          {loadPhase === "loadingMore" && hasMore && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/80" />
          )}
        </div>
      </div>
      <div ref={observerTarget} className="h-px w-full" aria-hidden />
    </div>
  );
};

export default EpisodeCards;
